import type { DatasetRow } from '@k-ucap/shared';

export type ExpressionValue = string | number | boolean | null;

export interface ExpressionContext {
  sequence: number;
  timestamp: string;
  runId?: string;
  sessionId?: string;
  vehicleId?: string;
  randomSeed?: string;
  datasetRow?: DatasetRow;
  parameterSet?: Record<string, unknown>;
}

const MAX_EXPRESSION_LENGTH = 256;

const safeTokenPattern = /^[a-zA-Z_][a-zA-Z0-9_:.]*$/;

const clamp = (min: number, max: number, value: number): number => Math.min(max, Math.max(min, value));

const isValidToken = (token: string): boolean => safeTokenPattern.test(token) && token.length <= 64;

const parseNumber = (value: unknown): number => {
  if (typeof value === 'number') return value;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const parseArgs = (args: string): string[] => {
  const result: string[] = [];
  let current = '';
  let inQuote = false;
  for (const char of args) {
    if (char === "'" || char === '"') {
      inQuote = !inQuote;
    } else if (char === ',' && !inQuote) {
      result.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }
  if (current.trim()) result.push(current.trim());
  return result;
};

export class ExpressionResolverService {
  private rngSeed?: string;

  setSeed(seed?: string): void {
    this.rngSeed = seed;
  }

  private mulberry32(seed: number): () => number {
    let state = seed >>> 0;
    return () => {
      state = (state + 0x6d2b79f5) >>> 0;
      let t = state;
      t = Math.imul(t ^ (t >>> 15), t | 1);
      t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  }

  private getRandom(ctx: ExpressionContext): () => number {
    const seed = this.rngSeed ?? ctx.randomSeed ?? `${ctx.sessionId ?? 'global'}-${ctx.sequence}`;
    let hash = 0;
    for (let i = 0; i < seed.length; i++) {
      hash = (hash << 5) - hash + seed.charCodeAt(i);
      hash |= 0;
    }
    return this.mulberry32(Math.abs(hash) + ctx.sequence);
  }

  resolve(expression: string, context: ExpressionContext): ExpressionValue {
    if (!expression || typeof expression !== 'string') return null;
    if (expression.length > MAX_EXPRESSION_LENGTH) {
      throw new Error('Expression exceeds maximum length');
    }
    const trimmed = expression.trim();
    if (!trimmed.startsWith('${') || !trimmed.endsWith('}')) {
      return expression;
    }
    const inner = trimmed.slice(2, -1).trim();
    return this.resolveInner(inner, context);
  }

  private resolveInner(inner: string, context: ExpressionContext): ExpressionValue {
    const open = (inner.match(/\(/g) || []).length;
    const close = (inner.match(/\)/g) || []).length;
    if (open !== close) {
      throw new Error(`Unbalanced parentheses in expression: ${inner}`);
    }

    const funcMatch = inner.match(/^([a-zA-Z_][a-zA-Z0-9_]*)\((.*)\)$/);
    if (funcMatch) {
      const name = funcMatch[1];
      const args = parseArgs(funcMatch[2]);
      return this.callFunction(name, args, context);
    }

    // Direct context variables (sequence, timestamp, runId, sessionId, vehicleId, randomSeed, datasetRow)
    if (isValidToken(inner) && inner in context) {
      return this.coerceValue(context[inner as keyof ExpressionContext]);
    }

    return this.resolveVariable(inner, context);
  }

  private callFunction(name: string, args: string[], context: ExpressionContext): ExpressionValue {
    if (!isValidToken(name)) {
      throw new Error(`Invalid function name: ${name}`);
    }
    const resolvedArgs = args.map((arg) => this.resolveValue(arg, context));

    switch (name) {
      case 'sequence':
        return context.sequence;
      case 'timestamp':
      case 'now':
        return context.timestamp;
      case 'uuid': {
        if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
          return crypto.randomUUID();
        }
        return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      }
      case 'random': {
        const [minArg, maxArg] = resolvedArgs;
        const min = parseNumber(minArg ?? 0);
        const max = parseNumber(maxArg ?? 1);
        const rnd = this.getRandom(context)();
        return min + rnd * (max - min);
      }
      case 'randomInt': {
        const [minArg, maxArg] = resolvedArgs;
        const min = Math.floor(parseNumber(minArg ?? 0));
        const max = Math.floor(parseNumber(maxArg ?? 1));
        const rnd = this.getRandom(context)();
        return Math.floor(min + rnd * (max - min + 1));
      }
      case 'clamp': {
        const [minArg, maxArg, valArg] = resolvedArgs;
        const min = parseNumber(minArg ?? 0);
        const max = parseNumber(maxArg ?? 0);
        const val = parseNumber(valArg ?? 0);
        return clamp(min, max, val);
      }
      case 'if': {
        const [condition, trueValue, falseValue] = resolvedArgs;
        return this.isTruthy(condition) ? trueValue : (falseValue ?? null);
      }
      case 'range': {
        const [startArg, stepArg] = resolvedArgs;
        const start = parseNumber(startArg ?? 0);
        const step = parseNumber(stepArg ?? 1);
        return start + context.sequence * step;
      }
      default:
        throw new Error(`Unsupported function: ${name}`);
    }
  }

  private isTruthy(value: unknown): boolean {
    if (value === null || value === undefined) return false;
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return !Number.isNaN(value) && value !== 0;
    if (typeof value === 'string') return value.trim().length > 0 && value !== 'false' && value !== '0';
    return true;
  }

  private resolveValue(token: string, context: ExpressionContext): ExpressionValue {
    const trimmed = token.trim();
    if ((trimmed.startsWith("'") && trimmed.endsWith("'")) || (trimmed.startsWith('"') && trimmed.endsWith('"'))) {
      return trimmed.slice(1, -1);
    }
    if (trimmed === 'true') return true;
    if (trimmed === 'false') return false;
    if (trimmed === 'null') return null;
    if (trimmed === 'undefined') return null;
    if (trimmed !== '' && !Number.isNaN(Number(trimmed))) {
      return Number(trimmed);
    }
    return this.resolveVariable(trimmed, context);
  }

  private resolveVariable(path: string, context: ExpressionContext): ExpressionValue {
    if (!isValidToken(path)) {
      throw new Error(`Invalid variable path: ${path}`);
    }
    const segments = path.split('.');
    const [prefix, ...rest] = segments;

    if (prefix === 'param') {
      const key = rest.join('.');
      const value = context.parameterSet?.[key];
      return value === undefined ? null : this.coerceValue(value);
    }
    if (prefix === 'dataset') {
      const key = rest.join('.');
      const value = context.datasetRow?.[key];
      return value === undefined ? null : this.coerceValue(value);
    }
    if (prefix === 'context') {
      const key = rest.join('.');
      const value = context[key as keyof ExpressionContext];
      return value === undefined ? null : this.coerceValue(value);
    }

    throw new Error(`Unknown variable prefix: ${prefix}`);
  }

  private coerceValue(value: unknown): ExpressionValue {
    if (value === null || value === undefined) return null;
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return Number.isNaN(value) ? 0 : value;
    return String(value);
  }

  validate(expression: string): { valid: boolean; error?: string } {
    try {
      this.resolve(expression, {
        sequence: 1,
        timestamp: new Date().toISOString(),
        parameterSet: {},
        datasetRow: {},
      });
      return { valid: true };
    } catch (err) {
      return { valid: false, error: err instanceof Error ? err.message : String(err) };
    }
  }
}

export const createExpressionResolverService = (): ExpressionResolverService => new ExpressionResolverService();
